import type {ReactElement} from 'react';
import {getAssetUrl} from '@Utils/s3.util';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {PlatformPostPreviewProps} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {getFeedMediaLayout} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {PreviewAccountHeader} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/preview-account-header';
import {PreviewAccountMetaSkeleton} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton';
import {PREVIEW_ACCOUNT_META_BAR_AND_SQUARE} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton.types';
import {PreviewAccountMenuDots} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-menu-dots/preview-account-menu-dots';
import type {FacebookCaptionSegment} from './facebook-post-preview.types';
import {FACEBOOK_ACTION_ICONS, FACEBOOK_CAPTION_HASHTAG_PATTERN, FACEBOOK_REACTION_LIKE_ICON_PATH, FACEBOOK_REACTION_LOVE_ICON_PATH} from './facebook-post-preview.types';
import styles from './facebook-post-preview.module.scss';
import {getCaptionWithPlaceholder} from '@Libraries/social-media-params.library';
import {PreviewClampedCaption} from '@Components/social-media/components/social-post-content-multiposting/components/preview-clamped-caption/preview-clamped-caption';

export function FacebookPostPreview({account, platformMediaType, publishingParams, numberOfPages, renderMedia}: PlatformPostPreviewProps): ReactElement {
  const captionText = getCaptionWithPlaceholder(publishingParams, account.id, platformMediaType);

  const isHashtagSegment = (segments: string[], segmentIndex: number): boolean => {
    const previousSegment = segments[segmentIndex - 1];
    return segments[segmentIndex].startsWith('#') && (!previousSegment || /\s$/u.test(previousSegment));
  };

  const getCaptionSegments = (): FacebookCaptionSegment[] => {
    const segments = captionText.split(FACEBOOK_CAPTION_HASHTAG_PATTERN);

    return segments
      .map((segmentText, segmentIndex) => {
        return {isHashtag: isHashtagSegment(segments, segmentIndex), text: segmentText};
      })
      .filter((segment) => segment.text !== '');
  };

  const getCaption = (): ReactElement => {
    return (
      <PreviewClampedCaption captionText={captionText} className={styles.facebookCaptionHolder} textClassName={styles.facebookCaptionText}>
        {getCaptionSegments().map((segment, segmentIndex) => {
          return <Text className={segment.isHashtag ? styles.facebookCaptionHashtag : styles.facebookCaptionBody} key={`${segment.text}-${segmentIndex}`} size={TextSize.XS} val={segment.text} />;
        })}
      </PreviewClampedCaption>
    );
  };

  const getActionIcons = (): ReactElement => {
    return (
      <div className={styles.facebookActionIcons}>
        {FACEBOOK_ACTION_ICONS.map((iconName) => {
          return <Icon ariaHidden className={styles.facebookActionIcon} icon={iconName} key={iconName} size={IconSize.SIZE_ICON_16} />;
        })}
      </div>
    );
  };

  const getReactionCluster = (): ReactElement => {
    return (
      <div className={styles.facebookReactionCluster}>
        <img alt="" aria-hidden="true" className={styles.facebookReactionLike} src={getAssetUrl(FACEBOOK_REACTION_LIKE_ICON_PATH)} />
        <img alt="" aria-hidden="true" className={styles.facebookReactionLove} src={getAssetUrl(FACEBOOK_REACTION_LOVE_ICON_PATH)} />
      </div>
    );
  };

  return (
    <div className={`${styles.facebookPost} _full-width`}>
      <div className={styles.facebookPostIntro}>
        <PreviewAccountHeader account={account} metaRow={<PreviewAccountMetaSkeleton parts={PREVIEW_ACCOUNT_META_BAR_AND_SQUARE} />} trailingAction={<PreviewAccountMenuDots />} />
        {getCaption()}
      </div>
      {renderMedia({layout: getFeedMediaLayout(numberOfPages), frameClassName: styles.facebookMediaFrame})}
      <div className={styles.facebookActionRow}>
        {getActionIcons()}
        {getReactionCluster()}
      </div>
    </div>
  );
}
